// frontend/src/components/Sidebar.tsx
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/Maria_Regna_Logotipo.png';
import { useAuth } from '../context/AuthContext';
import { UserRole, hasPermission } from '../types';

interface MenuItem {
  name: string;
  icon: JSX.Element;
  path: string;
  roles: UserRole[];
  privilege?: string;
}

interface SidebarProps {
  isOpen?: boolean;
  isDesktopOpen?: boolean;
  onClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, isDesktopOpen = true, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const allMenuItems: MenuItem[] = [
    {
      name: 'Dashboard',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      ),
      path: '/dashboard',
      roles: [UserRole.ADMIN],
      privilege: 'dashboard:read'
    },
    {
      name: 'Nota de Venta',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      path: '/ventas',
      roles: [UserRole.ADMIN, UserRole.VENDEDORA, UserRole.VENDEDORA_EN_LINEA],
      privilege: 'sales:read'
    },
    {
      name: 'Apartados',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      path: '/apartados',
      roles: [UserRole.ADMIN, UserRole.VENDEDORA, UserRole.VENDEDORA_EN_LINEA],
      privilege: 'sales:read'
    },
    {
      name: 'Inventario',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
        </svg>
      ),
      path: '/inventario',
      roles: [UserRole.ADMIN, UserRole.VENDEDORA, UserRole.VENDEDORA_EN_LINEA],
      privilege: 'products:read'
    },
    {
      name: 'Pre-orders',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      path: '/pre-orders',
      roles: [UserRole.ADMIN, UserRole.VENDEDORA, UserRole.VENDEDORA_EN_LINEA],
      privilege: 'products:read'
    },
    {
      name: 'Almacenes',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      path: '/almacenes',
      roles: [UserRole.ADMIN]
    },
    {
      name: 'Tiendas',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-4.5 0H2.36m11.14 0h4.14M2.36 21V9.359a1.125 1.125 0 01.32-.792l7.5-7.5a1.125 1.125 0 011.58 0l7.5 7.5c.21.21.32.497.32.792V21M2.36 21h15.28" />
        </svg>
      ),
      path: '/stores',
      roles: [UserRole.ADMIN, UserRole.VENDEDORA, UserRole.VENDEDORA_EN_LINEA]
    },
    {
      name: 'Compras',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      ),
      path: '/compras',
      roles: [UserRole.ADMIN, UserRole.VENDEDORA, UserRole.VENDEDORA_EN_LINEA]
    },
    {
      name: 'Proveedores',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      path: '/proveedores',
      roles: [UserRole.ADMIN],
      privilege: 'products:read'
    },
    {
      name: 'Costuras',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z" />
        </svg>
      ),
      path: '/tickets',
      roles: [UserRole.ADMIN, UserRole.VENDEDORA, UserRole.VENDEDORA_EN_LINEA, UserRole.MODISTA],
      privilege: 'tickets:read'
    },
    {
      name: 'Comisiones',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
      path: '/comisiones',
      roles: [UserRole.ADMIN],
      privilege: 'payments:read'
    },
    {
      name: 'Usuarios',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      path: '/usuarios',
      roles: [UserRole.ADMIN],
      privilege: 'users:read'
    },
    {
      name: 'Reportes',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      path: '/reportes',
      roles: [UserRole.ADMIN],
      privilege: 'reports:read'
    },
    {
      name: 'Clientes',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      path: '/clientes',
      roles: [UserRole.ADMIN, UserRole.VENDEDORA, UserRole.VENDEDORA_EN_LINEA],
      privilege: 'clients:read'
    }
  ];

  // Función para verificar si el usuario tiene acceso a un ítem del menú
  const hasAccess = (item: MenuItem): boolean => {
    if (!user) return false;

    // Verificar si el rol del usuario está permitido para este ítem
    if (!item.roles.includes(user.rol_usr as UserRole)) {
      return false;
    }

    // Si requiere privilegio, verificarlo usando user.rol_usr y hasPermission del sistema
    if (item.privilege) {
      if (hasPermission(user.rol_usr as UserRole, item.privilege)) {
        return true;
      }
      return false;
    }

    // Si no requiere privilegio, el acceso por rol es suficiente
    return true;
  };

  // Filtrar menú según el rol del usuario y sus privilegios
  const menuItems = user
    ? allMenuItems.filter(item => hasAccess(item))
    : [];

  // Function to determine if menu item is active
  const isActive = (path: string) => {
    if (path.includes('?')) {
      const [pathUrl, query] = path.split('?');
      return location.pathname === pathUrl && location.search === `?${query}`;
    }
    if (location.search && location.pathname === path) {
      return false;
    }
    return location.pathname === path;
  };

  return (
    <>
      {/* Mobile Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-20 transition-opacity lg:hidden ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        onClick={onClose}
      />

      {/* Sidebar Container */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-30 w-64 bg-[#2d3748] text-white flex flex-col transition-transform duration-300 ease-in-out
          lg:static lg:h-screen
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isDesktopOpen ? 'lg:translate-x-0' : 'lg:hidden'}
        `}
      >
        {/* Logo */}
        <div className="p-6 border-b border-gray-700 flex justify-between items-start">
          <div className="flex flex-col items-center w-full">
            <img src={logo} alt="María Regna" className="h-20 w-auto mb-2" />
            <div className="text-center">
              <p className="text-xs text-gray-400">
                {user?.rol_usr === UserRole.ADMIN && 'Panel de Administración'}
                {(user?.rol_usr === UserRole.VENDEDORA || user?.rol_usr === UserRole.VENDEDORA_EN_LINEA) && 'Panel de Ventas'}
                {user?.rol_usr === UserRole.MODISTA && 'Panel de Costuras'}
              </p>
            </div>
          </div>
          {/* Mobile Close Button */}
          <button onClick={onClose} className="lg:hidden text-gray-400 hover:text-white absolute top-4 right-4">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-6 overflow-y-auto sidebar-scroll">
          <ul className="space-y-1 px-3">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={() => {
                    // Close sidebar on mobile when item is clicked
                    if (window.innerWidth < 1024 && onClose) {
                      onClose();
                    }
                  }}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg
                    transition-all duration-200
                    ${isActive(item.path)
                      ? 'bg-[#4a5568] text-white font-medium'
                      : 'text-gray-300 hover:bg-[#4a5568] hover:text-white'
                    }
                  `}
                >
                  <span className={isActive(item.path) ? 'text-white' : 'text-gray-400'}>
                    {item.icon}
                  </span>
                  <span className="text-sm">{item.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700">
          {/* User Info (Visible only on Mobile/Tablet) */}
          <div className="lg:hidden flex items-center space-x-3 px-4 py-4 mb-2 bg-gray-800/30 rounded-xl">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
              {user?.email ? getInitials(user.email) : 'AD'}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-gray-200 truncate">
                {user?.rol_usr || 'Usuario'}
              </p>
              <p className="text-xs text-gray-400 truncate">{user?.username || user?.email || ''}</p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 hover:text-red-300 transition-all duration-200 mb-4 lg:hidden"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span className="text-sm font-medium">Cerrar Sesión</span>
          </button>

          <div className="text-xs text-gray-400 text-center">
            <p>© ERP MariRegna v1.0.0</p>
            <p className="mt-1">Desarrollado por <a href="https://pagedraftstudio.com" className="underline">PageDraft Studio</a></p>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;